import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { useSnackbar } from 'notistack';
import ReadBlocks from './ReadBlocks';
import ReadFormComponent from './ReadFormComponent';
import AutoMapper from './AutoMapper';
import { dataModelLoadData } from "../../../api's/DataModelApi";
import LoadingIcon from '../../../reusable-component/LoadingIcon';
import { createDataProcessorJson } from '../utils/JsonCreation';

const steps = ['Read Blocks', 'Read Form', 'Auto Mapper'];

function LoadDataModal({ show, onClose, onSave, attributes, selectedEntityId }) {
  const [selectedReadBlock, setSelectedReadBlock] = useState({});
  const [pipelineName, setPipelineName] = useState('');
  const [dataModelformField, setDataModelformField] = useState({
    mappings: [],
  });
  const readNodeId = `dndnode_${+new Date()}`;
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (!show) {
      setActiveStep(0);
      setSelectedReadBlock({});
      setDataModelformField({});
    }
  }, [show]);

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <ReadBlocks
            selectedReadBlock={selectedReadBlock}
            setSelectedReadBlock={setSelectedReadBlock}
            pipelineName={pipelineName}
            setPipelineName={setPipelineName}
          />
        );
      case 1:
        return (
          <ReadFormComponent
            readNodeId={readNodeId}
            dataModelformField={dataModelformField}
            setDataModelformField={setDataModelformField}
            selectedReadBlock={selectedReadBlock}
          />
        );
      case 2:
        return (
          <AutoMapper
            dataModelformField={dataModelformField}
            setDataModelformField={setDataModelformField}
            selectedReadBlock={selectedReadBlock}
            attributes={attributes}
          />
        );
      default:
        return null;
    }
  };

  const handleSubmit = async () => {
    setLoading(true);

    const updatedDataProcessorJson = createDataProcessorJson({
      dataModelformField,
      selectedEntityId,
    });

    console.log('dataProcessorJson: ', updatedDataProcessorJson);

    try {
      const payload = {
        entityId: selectedEntityId,
        pipelineName,
        dataProcessorJson: updatedDataProcessorJson,
      };
      const response = await dataModelLoadData(payload);
      if (response.status === 200) {
        setLoading(false);
        onClose();
      }
    } catch (error) {
      enqueueSnackbar('Something went wrong!', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (!pipelineName.trim()) {
        enqueueSnackbar('Pipeline name is required!', {
          variant: 'error',
        });
        return;
      }

      if (pipelineName.trim().length > 35) {
        enqueueSnackbar('Pipeline name must be less than 35 characters!', {
          variant: 'error',
        });
        return;
      }

      if (!selectedReadBlock || !selectedReadBlock.key) {
        enqueueSnackbar('Please select a read block!', {
          variant: 'error',
        });
        return;
      }
      setDataModelformField({
        mappings: [],
      });
    }

    if (activeStep === 1) {
      const source = dataModelformField?.source?.formField;
      const fetchedHeader = dataModelformField?.source?.fetchedHeader;

      if (!source?.alias?.trim()) {
        enqueueSnackbar('Alias is required!', {
          variant: 'error',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
        return;
      }
      const readPathBlocks = ['ReadCSV', 'ReadCSVAws'];
      if (readPathBlocks.some((block) => block === source.nodeName) && !source.path) {
        enqueueSnackbar('Path is required!', {
          variant: 'error',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
        return;
      }
      if (!fetchedHeader || fetchedHeader.length === 0) {
        enqueueSnackbar('Please upload or fetch headers first!', {
          variant: 'error',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
        return;
      }
    }

    if (activeStep === steps.length - 1) {
      handleSubmit();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  const getButtonLabel = () => {
    if (activeStep === steps.length - 1) return 'Save';
    if (activeStep === 1) return 'Save and Next';
    return 'Next';
  };

  return (
    <Dialog
      open={show}
      onClose={onClose}
      maxWidth="md"
      PaperProps={{
        sx: { minWidth: '70vw' },
      }}
      fullWidth
    >
      <DialogTitle>Load Data</DialogTitle>

      <DialogContent dividers>{renderStepContent()}</DialogContent>
      {loading && <LoadingIcon />}
      <DialogActions>
        {activeStep === 0 && (
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
        )}
        {activeStep > 0 && (
          <Button onClick={handleBack} color="inherit">
            Back
          </Button>
        )}
        <Button variant="contained" onClick={handleNext}>
          {getButtonLabel()}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default LoadDataModal;
